import { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";

// Material Dashboard 2 PRO components
import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";
import PlanCard from "parts/Cards/PlanCard";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import decor1Image from "/images/story-card/makimono.png";
import decor2Image from "/images/story-card/syodo.png";
import decor3Image from "/images/story-card/shishiodo.png";

import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";
import { useNotification } from "context/NotificationContext";

import { getAiPlans, AiPlan } from "@shared/api/aiPlans";

function AIPlanResult() {
  const { t } = useContext(LanguageContext)  as any;
  const { session } = useSessionContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();

  const [aiPlans, setAiPlans] = useState<AiPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isWaitingForInitialPlan, setIsWaitingForInitialPlan] = useState(
    Boolean(location.state?.isCreatingPlan)
  );

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };
     window.addEventListener("resize", handleResize);
     handleResize();
     return () => window.removeEventListener("resize", handleResize);
   }, []);

  const fetchPlans = async () => {
    try {
      if (!session?.access_token) {
        return;
      }
      const data = await getAiPlans(session.access_token);
      console.log(`fetch get AiPlans data.length::${data.length}`);
      if (!data || (Array.isArray(data) && data.length === 0)) {
        setAiPlans([]);
      } else {
        setAiPlans(data);
        setIsWaitingForInitialPlan(false);
      }
    } catch (err) {
      console.error("AIプランの取得に失敗:", err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.get-error"),
        },
        3000
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [session?.access_token]);

  useEffect(() => {
    if (location.state?.isCreatingPlan) {
      setIsWaitingForInitialPlan(true);
    }
  }, [location.state?.isCreatingPlan]);

  useEffect(() => {
    const hasCreatingPlan = isWaitingForInitialPlan || aiPlans.some((plan) => String(plan.status) === "0");
    if (!hasCreatingPlan) return undefined;

    const intervalId = window.setInterval(() => {
      fetchPlans();
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [aiPlans, isWaitingForInitialPlan]);
  
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDTypography variant="h4" mb={3}>
          {t("AIPlanResult.title")}
        </MDTypography>
        {!loading && aiPlans.length === 0 && isWaitingForInitialPlan ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8} lg={6} mx="auto">
              <Card sx={{ p: { xs: 3, sm: 5 }, textAlign: "center", border: "1px solid #dce8fa", background: "linear-gradient(145deg, #ffffff, #f5f8fe)" }}>
                <MDBox sx={{ width: 112, height: 112, borderRadius: "50%", backgroundColor: "#eef5ff", border: "1px solid #d6e5fc", display: "grid", placeItems: "center", mx: "auto", mb: 2 }}>
                  <MDBox sx={{ width: 68, height: 68, borderRadius: 3, backgroundColor: "#2f76e8", border: "6px solid #e6f0ff", display: "grid", placeItems: "center", boxShadow: "0 10px 20px rgba(47,118,232,.25)" }}>
                    <AutoAwesomeIcon sx={{ color: "#ffffff", fontSize: 34 }} />
                  </MDBox>
                </MDBox>
                <MDBox display="flex" justifyContent="center" alignItems="center" gap={1} mb={1}>
                  <CircularProgress size={17} color="info" />
                  <MDTypography variant="button" fontWeight="bold" color="info">
                    {t("AI-Coaching.goalWizard.result.progressLabel")}
                  </MDTypography>
                </MDBox>
                <MDTypography variant="h4" fontWeight="bold" color="dark" mb={1}>
                  {t("AI-Coaching.goalWizard.result.headline")}
                </MDTypography>
                <MDTypography variant="body2" color="secondary" mb={3}>
                  {t("AI-Coaching.goalWizard.result.reassurance")}
                </MDTypography>
                <MDButton variant="outlined" color="info" onClick={fetchPlans} startIcon={<RefreshIcon />}>
                  {t("Aiplan.refresh")}
                </MDButton>
              </Card>
            </Grid>
          </Grid>
        ) : !loading && aiPlans.length === 0 ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8} lg={6} mx="auto">
              <Card sx={{ p: 4, textAlign: "center" }}>
                <MDTypography variant="h5" fontWeight="bold" mb={2}>
                  {t("AIPlanResult.no_plan")}
                </MDTypography>
                <MDTypography variant="body1" color="text" mb={4}>
                  {t("AIPlanResult.message")}
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => navigate("/pages/ai-coaching/GoalSetting")}
                >
                  {t("AIPlanResult.back")}
                </MDButton>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            spacing={3}
            justifyContent={aiPlans.length === 1 ? "center" : "flex-start"}
          >
            {aiPlans.map((plan, index) => {
              const image =
                index === 0
                  ? decor1Image
                  : index === 1
                    ? decor2Image
                    : index === 2
                      ? decor3Image
                      : decor1Image;

              return (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <PlanCard
                    id={plan.id}
                    image={image}
                    title={plan.title}
                    description={plan.description}
                    category={plan.category}
                    status={plan.status}
                    is_accepted={plan.is_accepted}
                    onDeleted={fetchPlans}
                    isMobile={isMobile}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AIPlanResult;
